import { Link, Navigate } from 'react-router-dom';
import { CheckSquare, Users, Check } from 'lucide-react';
import { Button, TaskEventDateIcon } from '@/components/ui';
import { useAuth } from '@/hooks';

export function Landing() {
  const { user, loading } = useAuth();

  // Authenticated users should never see the marketing page — bounce them
  // into the app. This guards against OAuth callbacks or stale links that
  // land authenticated users here (e.g. catch-all fallback after Supabase
  // PKCE exchange completes asynchronously).
  if (!loading && user) {
    return (
      <Navigate
        to="/today"
        replace
      />
    );
  }

  return (
    <div className="min-h-dvh bg-surface">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white border-b border-border px-4 sm:px-8 py-4 flex items-center justify-between z-[100]">
        <Link
          to="/landing"
          className="flex items-center gap-2 no-underline"
        >
          <img
            src="/logo.svg"
            alt="Yaruta"
            className="h-8 w-8"
          />
          <span className="text-xl font-bold text-primary">Yaruta</span>
        </Link>
        <div className="flex items-center gap-4">
          {/* Privacy/Terms duplicate the footer links and only matter on
              desktop. Hiding them below `sm` keeps the mobile header
              from wrapping the right cluster into the logo column. */}
          <Link
            to="/privacy"
            className="hidden sm:inline text-sm text-muted-foreground no-underline hover:text-foreground"
          >
            Privacy
          </Link>
          <Link
            to="/terms"
            className="hidden sm:inline text-sm text-muted-foreground no-underline hover:text-foreground"
          >
            Terms
          </Link>
          <Link to="/">
            <Button
              variant="primary"
              size="sm"
            >
              Get Started
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-light to-white pt-24 pb-24 px-8 text-center">
        <h1 className="text-5xl font-bold text-foreground mb-6 leading-tight">
          Yaruta — Simplify Family Management
        </h1>
        <p className="text-xl text-muted-foreground max-w-xl mx-auto mb-8">
          Organize tasks, coordinate schedules, and keep your family connected
          all in one place.
        </p>
        <div className="flex gap-4 justify-center">
          <Link to="/">
            <Button
              variant="primary"
              size="lg"
            >
              Get Started Free
            </Button>
          </Link>
          <Link to="/">
            <Button
              variant="outline"
              size="lg"
            >
              Learn More
            </Button>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-8 px-8 py-16 max-w-[1000px] mx-auto">
        <div className="text-center p-8">
          <div className="w-12 h-12 mx-auto mb-4 text-primary">
            <CheckSquare size={48} />
          </div>
          <h3 className="text-lg font-semibold mb-2">Task Management</h3>
          <p className="text-sm text-muted-foreground">
            Assign and track tasks for every family member with ease.
          </p>
        </div>

        <div className="text-center p-8">
          <div className="w-12 h-12 mx-auto mb-4 text-primary">
            <TaskEventDateIcon size={48} />
          </div>
          <h3 className="text-lg font-semibold mb-2">Calendar Sync</h3>
          <p className="text-sm text-muted-foreground">
            Keep everyone's schedules synchronized and avoid conflicts.
          </p>
        </div>

        <div className="text-center p-8">
          <div className="w-12 h-12 mx-auto mb-4 text-primary">
            <Users size={48} />
          </div>
          <h3 className="text-lg font-semibold mb-2">Family Coordination</h3>
          <p className="text-sm text-muted-foreground">
            Communicate and coordinate with all family members seamlessly.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-white py-20 px-8">
        <h2 className="text-center text-[2rem] font-bold mb-12">
          How It Works
        </h2>
        <div className="flex justify-center gap-12 max-w-[900px] mx-auto flex-wrap">
          <div className="text-center max-w-[250px]">
            <div className="w-12 h-12 rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">
              1
            </div>
            <h3 className="text-lg font-semibold mb-2">Sign Up</h3>
            <p className="text-sm text-muted-foreground">
              Create your account and set up your family profile
            </p>
          </div>
          <div className="text-center max-w-[250px]">
            <div className="w-12 h-12 rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">
              2
            </div>
            <h3 className="text-lg font-semibold mb-2">Add Members</h3>
            <p className="text-sm text-muted-foreground">
              Invite family members and assign roles
            </p>
          </div>
          <div className="text-center max-w-[250px]">
            <div className="w-12 h-12 rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">
              3
            </div>
            <h3 className="text-lg font-semibold mb-2">Start Organizing</h3>
            <p className="text-sm text-muted-foreground">
              Create tasks, set schedules, and stay connected
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-muted py-20 px-8">
        <h2 className="text-center text-[2rem] font-bold mb-4">
          Simple Pricing
        </h2>
        <p className="text-center text-muted-foreground mb-12">
          Choose the plan that works for your family
        </p>
        <div className="flex justify-center gap-8 flex-wrap">
          <div className="bg-white border border-border rounded-md p-8 w-[280px] text-center">
            <div className="text-xl font-semibold mb-2">Free</div>
            <div className="text-4xl font-bold text-primary">
              $0
              <span className="text-base text-muted-foreground font-normal">
                /month
              </span>
            </div>
            <ul className="list-none my-6 text-left flex flex-col">
              <li className="flex items-center gap-2 text-sm text-foreground py-2">
                <Check
                  size={16}
                  className="text-success shrink-0"
                />
                Up to 5 family members
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground py-2">
                <Check
                  size={16}
                  className="text-success shrink-0"
                />
                Basic task management
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground py-2">
                <Check
                  size={16}
                  className="text-success shrink-0"
                />
                Calendar sync
              </li>
            </ul>
            <Link to="/">
              <Button
                variant="outline"
                fullWidth
              >
                Get Started
              </Button>
            </Link>
          </div>

          <div className="bg-white border-2 border-primary rounded-md p-8 w-[280px] text-center shadow-md relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-semibold px-3 py-1 rounded-full">
              Most Popular
            </span>
            <div className="text-xl font-semibold mb-2">Pro</div>
            <div className="text-4xl font-bold text-primary">
              $9
              <span className="text-base text-muted-foreground font-normal">
                /month
              </span>
            </div>
            <ul className="list-none my-6 text-left flex flex-col">
              <li className="flex items-center gap-2 text-sm text-foreground py-2">
                <Check
                  size={16}
                  className="text-success shrink-0"
                />
                Unlimited family members
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground py-2">
                <Check
                  size={16}
                  className="text-success shrink-0"
                />
                Advanced task management
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground py-2">
                <Check
                  size={16}
                  className="text-success shrink-0"
                />
                Priority support
              </li>
              <li className="flex items-center gap-2 text-sm text-foreground py-2">
                <Check
                  size={16}
                  className="text-success shrink-0"
                />
                Custom integrations
              </li>
            </ul>
            <Link to="/">
              <Button
                variant="primary"
                fullWidth
              >
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-muted py-12 px-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <img
            src="/logo.svg"
            alt="Yaruta"
            className="h-7 w-7 brightness-0 invert"
          />
          <span className="text-xl font-bold text-white">Yaruta</span>
        </div>
        <div className="flex justify-center gap-6 mb-4">
          <Link
            to="/privacy"
            className="text-sm text-muted-foreground no-underline hover:text-white"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="text-sm text-muted-foreground no-underline hover:text-white"
          >
            Terms & Conditions
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} Yaruta. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
